import './index.scss'
import {useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import {DateRange as DR} from 'react-date-range'
import {DateIcon} from '../../../icons'
import useOutsideClick from '../../../utils/hooks/useOutsideClick'
import classNames from 'classnames'
import TimePicker from 'rc-time-picker'
import Input from '../input'
import regexPatterns from '../../../utils/regexPatterns'

const DEFAULT_MS_VALUE = '00.000'
const DOT = '.'
const DOT_INDEX = 2

const START = 0
const PLACEHOLDER_CHAR = '0'

const isFullSelection = (selectionStart, selectionEnd, inputValue) => {
    return selectionStart === START && selectionEnd === inputValue.length
}

const getNextCursorPosition = (pos) => {
    return pos === DOT_INDEX || pos === START ? pos + DOT_INDEX : pos + 1
}

const getBeforeCursorPosition = (pos) => {
    return pos === DOT_INDEX ? pos : pos + 1
}

const DateRange = ({
    placeholder,
    state,
    onChange,
    className,
    children,
    hasTimePicker = false,
    startTime,
    showMs = false,
    startFloatSec,
    setStartFloatSec,
    endFloatSec,
    setEndFloatSec,
    endTime,
    onChangeStartTime,
    onChangeEndTime
}) => {
    const rangeSelected = useSelector(state => state.monitoring.rangeSelected)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const [isActive, setIsActive] = useState(false)
    const ref = useRef()
    useOutsideClick(ref, () => setIsActive(false))

    const handleChange = item => {
        onChange([item.selection])
    }

    const handleSecondsInputChange = (
        e,
        floatSec,
        setFloatSec
    ) => {
        const input = e.target
        const key = e.nativeEvent.data
        const cursorStart = input.selectionStart
        const cursorEnd = input.selectionEnd
        const currentPos = cursorStart - 1
        const chars = floatSec.split('')

        const fullSelection = isFullSelection(cursorStart, cursorEnd, input.value)
        const isBackspace = !key && currentPos >= -1 && currentPos < chars.length - 1
        const isDigit = key?.match(regexPatterns.onlyNumber)
        const targetIndex = currentPos === START ? currentPos : currentPos - 1
        const isEditable = chars[targetIndex] !== DOT && targetIndex >= 0 && targetIndex < chars.length

        if (!key && fullSelection) {
            const resetValue = DEFAULT_MS_VALUE
            setFloatSec(resetValue)
            return requestAnimationFrame(() => {
                input.setSelectionRange(START, START)
            })
        }

        if (isBackspace && chars[currentPos + 1] !== DOT) {
            chars[currentPos + 1] = PLACEHOLDER_CHAR
            const updatedValue = chars.join('')
            setFloatSec(updatedValue)

            const newCursorPos = getBeforeCursorPosition(currentPos)
            return requestAnimationFrame(() => {
                input.setSelectionRange(newCursorPos, newCursorPos)
            })
        }

        if (!isDigit || !isEditable) return

        chars[targetIndex] = key
        const updatedValue = chars.join('')
        setFloatSec(updatedValue)

        const nextCursor = getNextCursorPosition(currentPos)
        requestAnimationFrame(() => {
            input.setSelectionRange(nextCursor, nextCursor)
        })
    }

    return (
        <div ref={ref}
            className={classNames('date-range', {
                'dark': isDarkMode,
                [className]: !!className,
                'active': isActive,
                'range-selected': rangeSelected
            })}
            onClick={() => setIsActive(!isActive)}>
            <p>{placeholder}</p>
            <span className="icon">
                <DateIcon/>
            </span>
            {isActive && (
                <div className={classNames('date-range-box', {
                    'dark': isDarkMode,
                    'with-time-picker': hasTimePicker
                })} onClick={e => e.stopPropagation()}>
                    <div className='date-range-block'>
                        <DR
                            editableDateInputs={true}
                            onChange={item => handleChange(item)}
                            moveRangeOnFirstSelection={true}
                            scroll={{enabled: true}}
                            maxDate={new Date()}
                            ranges={state}
                        />
                        {hasTimePicker && (
                            <span className={classNames('time-picker-box', {'dark': isDarkMode})}>
                                <div className="time-with-hours">
                                    <TimePicker
                                        showSecond={false}
                                        inputReadOnly
                                        className={classNames({
                                            'without-ms': !showMs
                                        })}
                                        onChange={value => {
                                            const float = parseFloat(startFloatSec)
                                            const secs = Math.floor(float)
                                            const ms = Math.round((float % 1) * 1000)
                                            const updated = value.clone().seconds(secs).milliseconds(ms)
                                            onChangeStartTime(updated)
                                        }}
                                        clearIcon={<></>}
                                        value={startTime}
                                        getPopupContainer={triggerNode => triggerNode.parentNode}
                                    />
                                    {showMs && <>
                                        <Input
                                            inputClassName="seconds"
                                            type="text"
                                            maxNumber={6}
                                            value={startFloatSec}
                                            onChange={e => handleSecondsInputChange(e, startFloatSec, setStartFloatSec)}
                                            className="float-sec-input"
                                        />
                                        <span className="suffix">ms</span>
                                    </>}
                                </div>
                                <div className="time-with-hours">
                                    <TimePicker
                                        showSecond={false}
                                        className={classNames({
                                            'without-ms': !showMs
                                        })}
                                        onChange={value => {
                                            const float = parseFloat(endFloatSec)
                                            const secs = Math.floor(float)
                                            const ms = Math.round((float % 1) * 1000)
                                            const updated = value.clone().seconds(secs).milliseconds(ms)
                                            onChangeEndTime(updated)
                                        }}
                                        clearIcon={<></>}
                                        inputReadOnly
                                        value={endTime}
                                        getPopupContainer={triggerNode => triggerNode.parentNode}
                                    />
                                    {showMs &&
                                    <>
                                        <Input
                                            inputClassName="seconds"
                                            type="text"
                                            value={endFloatSec}
                                            maxNumber={6}
                                            onChange={e => handleSecondsInputChange(e, endFloatSec, setEndFloatSec)}
                                            className="float-sec-input"
                                        />
                                        <span className="suffix">ms</span>
                                    </>
                                    }

                                </div>
                            </span>
                        )}
                    </div>
                    <div className="children">
                        {children}
                    </div>
                </div>
            )}
        </div>
    )
}

export default DateRange
